/**
 * Lifecycle guard for private browser caches and BFCache snapshots.
 *
 * Private HTML/API bytes must not remain recoverable after logout, terminal
 * authentication expiry, or an account/role change in the same browser tab.
 */
(function () {
    "use strict";

    var OWNER_KEY = "kanal:private-browser-cache-owner:v1";
    var LOGOUT_PENDING_KEY = "kanal:private-browser-cache-logout-pending:v1";
    var LOGIN_PENDING_KEY = "kanal:private-browser-cache-login-pending:v1";
    var PURGE_SIGNAL_KEY = "kanal:private-browser-cache-purge:v1";
    var PURGE_CHANNEL_NAME = "kanal-private-browser-cache-v1";
    var LOGOUT_COMPLETION_HASH = "#logout-complete";
    var OWNER_ENDPOINT = "/api/v1/users/me/browser-owner";
    var OWNER_ROLE_PATTERN = /^[a-z0-9_-]{1,32}$/;
    var OWNER_REVALIDATION_TIMEOUT_MS = 5000;
    var AUTH_TRANSITION_MAX_AGE_MS = 35000;
    var LOGOUT_REVALIDATION_DELAYS_MS = Object.freeze([
        250,
        750,
        2000,
        5000,
        10000,
        17000
    ]);
    var PRIVATE_SESSION_EXACT_KEYS = Object.freeze([
        OWNER_KEY,
        LOGOUT_PENDING_KEY,
        "kaspi_client_error_queue_v1"
    ]);
    var PRIVATE_SESSION_PREFIXES = Object.freeze([
        "kanal:nav-swr:",
        "inventory-snapshot:",
        // Без номера версии намеренно: ключ уже бампали v4 -> v5, и записи
        // прошлой версии перестали вычищаться при выходе — приватные данные
        // оставались в браузере ровно там, где этот модуль обязан их стирать.
        "dashboard_batch_",
        "cargo_calculator_"
    ]);
    var PRIVATE_LOCAL_EXACT_KEYS = Object.freeze([
        "htmx-history-cache",
        "purchase_favorites"
    ]);
    var PRIVATE_LOCAL_PREFIXES = Object.freeze([
        "api_cache:",
        "dashboard_advanced_cache:",
        "dashboard_insights_cache:",
        "orders_status_counts_v1:"
    ]);
    var RETIRED_LOCAL_PREFIXES = Object.freeze(["api_cache:"]);
    var bodyHiddenBeforeBfcache = null;
    var externalPurgeHandled = false;
    var handledSignalNonces = [];
    var logoutPendingSent = false;
    var logoutVerificationEpoch = 0;
    var purgeChannel = null;

    function getStorage(name) {
        try {
            return window[name] || null;
        } catch (_error) {
            return null;
        }
    }

    function normalizedOwner(userId, roleValue) {
        var userIdNumber = Number(userId);
        var role = String(roleValue || "").trim().toLowerCase();
        if (!Number.isSafeInteger(userIdNumber) || userIdNumber <= 0) return null;
        if (!OWNER_ROLE_PATTERN.test(role)) return null;
        return String(userIdNumber) + ":" + role;
    }

    function currentOwner() {
        return normalizedOwner(window.currentUserId, window.userRole);
    }

    function documentAuthState() {
        var body = document.body;
        var state = body && body.dataset
            ? String(body.dataset.browserAuthState || "").toLowerCase()
            : "";
        if (state === "authenticated" || state === "anonymous") return state;
        return "unknown";
    }

    function removeMatchingKeys(store, exactKeys, prefixes) {
        if (!store) return;
        try {
            var keys = [];
            for (var index = 0; index < store.length; index += 1) {
                var key = store.key(index);
                if (!key) continue;
                if (
                    exactKeys.indexOf(key) !== -1
                    || prefixes.some(function (prefix) { return key.indexOf(prefix) === 0; })
                ) {
                    keys.push(key);
                }
            }
            keys.forEach(function (key) { store.removeItem(key); });
        } catch (_error) {
            // Restricted storage must never block logout/authentication recovery.
        }
    }

    function clearPrivateSessionCaches() {
        removeMatchingKeys(
            getStorage("sessionStorage"),
            PRIVATE_SESSION_EXACT_KEYS,
            PRIVATE_SESSION_PREFIXES
        );
    }

    function clearPrivateLocalCaches() {
        removeMatchingKeys(
            getStorage("localStorage"),
            PRIVATE_LOCAL_EXACT_KEYS,
            PRIVATE_LOCAL_PREFIXES
        );
    }

    function clearRetiredLocalCaches() {
        removeMatchingKeys(getStorage("localStorage"), [], RETIRED_LOCAL_PREFIXES);
    }

    function clearServiceWorkerAuthCache() {
        try {
            if (navigator.serviceWorker && navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({ type: "CLEAR_AUTH_CACHE" });
            }
        } catch (_error) {
            // A missing/restricted service worker must not block cache cleanup.
        }
    }

    function clearPrivateBrowserCaches() {
        clearPrivateSessionCaches();
        clearPrivateLocalCaches();
        clearServiceWorkerAuthCache();
    }

    function crossTabSignalPayload(type, reason) {
        return {
            type: type,
            reason: String(reason || "private_cache_invalidation").slice(0, 64),
            owner: currentOwner(),
            at: Date.now(),
            nonce: Math.random().toString(36).slice(2)
        };
    }

    function broadcastCrossTabSignal(type, reason) {
        var payload = crossTabSignalPayload(type, reason);
        try {
            if (purgeChannel) purgeChannel.postMessage(payload);
        } catch (_error) {
            // The storage event below remains a cross-tab fallback.
        }
        try {
            var localStore = getStorage("localStorage");
            if (localStore) localStore.setItem(PURGE_SIGNAL_KEY, JSON.stringify(payload));
        } catch (_error) {
            // Restricted storage must not prevent same-tab clearing.
        }
        return payload;
    }

    function broadcastPrivateCachePurge(reason) {
        return broadcastCrossTabSignal("PURGE_PRIVATE_CACHES", reason);
    }

    function broadcastLogoutPending() {
        return broadcastCrossTabSignal("LOGOUT_PENDING", "logout_pending");
    }

    function signalAlreadyHandled(payload) {
        if (!payload || typeof payload !== "object") return true;
        var nonce = String(payload.nonce || "");
        if (!nonce || nonce.length > 64) return true;
        if (handledSignalNonces.indexOf(nonce) !== -1) return true;
        handledSignalNonces.push(nonce);
        if (handledSignalNonces.length > 16) handledSignalNonces.shift();
        return false;
    }

    function parsedStorageSignal(rawValue) {
        if (!rawValue || typeof rawValue !== "string" || rawValue.length > 1024) return null;
        try {
            var payload = JSON.parse(rawValue);
            return payload && typeof payload === "object" ? payload : null;
        } catch (_error) {
            return null;
        }
    }

    function storedValue(store, key) {
        if (!store) return null;
        try {
            return store.getItem(key);
        } catch (_error) {
            return null;
        }
    }

    function removeStoredValue(store, key) {
        if (!store) return;
        try {
            store.removeItem(key);
        } catch (_error) {
            // Restricted storage is handled by the fail-closed runtime path.
        }
    }

    function setStoredValue(store, key, value) {
        if (!store) return false;
        try {
            store.setItem(key, value);
            return true;
        } catch (_error) {
            return false;
        }
    }

    function transitionMarker(reason) {
        return JSON.stringify({
            reason: reason,
            owner: currentOwner(),
            at: Date.now(),
            nonce: Math.random().toString(36).slice(2)
        });
    }

    function validTransitionMarker(rawValue, expectedReason) {
        if (!rawValue || typeof rawValue !== "string" || rawValue.length > 512) return null;
        try {
            var marker = JSON.parse(rawValue);
            var age = Date.now() - Number(marker.at);
            if (
                !marker
                || marker.reason !== expectedReason
                || !Number.isFinite(age)
                || age < -5000
                || age > AUTH_TRANSITION_MAX_AGE_MS
            ) return null;
            return marker;
        } catch (_error) {
            return null;
        }
    }

    function stripLogoutCompletionHash() {
        try {
            if (window.history && typeof window.history.replaceState === "function") {
                window.history.replaceState(
                    null,
                    document.title,
                    String(window.location.pathname || "/")
                        + String(window.location.search || "")
                );
            }
        } catch (_error) {
            // The fragment carries no credential and is safe to leave behind.
        }
    }

    function completeServerConfirmedLogout() {
        var hash = "";
        try {
            hash = String(window.location.hash || "");
        } catch (_error) {
            return;
        }
        if (
            documentAuthState() !== "anonymous"
            || currentOwner()
            || hash !== LOGOUT_COMPLETION_HASH
        ) return;

        // This marker is only issued by the POST /logout response after token
        // revocation and cookie deletion. Broadcasting earlier would let
        // another tab reload while the old session was still valid.
        broadcastPrivateCachePurge("logout_complete");
        stripLogoutCompletionHash();
    }

    function completeServerConfirmedLogin() {
        if (documentAuthState() !== "authenticated") return;
        var owner = currentOwner();
        if (!owner) return;
        var sessionStore = getStorage("sessionStorage");
        var rawMarker = storedValue(sessionStore, LOGIN_PENDING_KEY);
        removeStoredValue(sessionStore, LOGIN_PENDING_KEY);
        if (!validTransitionMarker(rawMarker, "login_pending")) return;

        // The authenticated page can only render after Set-Cookie was applied.
        // This also covers a new tab whose sessionStorage had no prior owner.
        broadcastPrivateCachePurge("login_complete");
    }

    function reloadAfterExternalPurge(payload) {
        if (externalPurgeHandled) return;
        externalPurgeHandled = true;
        logoutVerificationEpoch += 1;
        clearPrivateBrowserCaches();
        if (documentAuthState() !== "authenticated") return;
        hideBodyForBfcache();
        try {
            window.location.reload();
        } catch (_error) {
            // Keep authenticated stale DOM hidden if navigation is blocked.
        }
    }

    function handleCrossTabSignal(payload) {
        if (signalAlreadyHandled(payload)) return;
        if (payload.type === "PURGE_PRIVATE_CACHES") {
            reloadAfterExternalPurge(payload);
            return;
        }
        if (payload.type === "LOGOUT_PENDING") {
            beginExternalLogoutVerification(payload);
        }
    }

    function storedOwner(store) {
        if (!store) return null;
        try {
            return store.getItem(OWNER_KEY);
        } catch (_error) {
            return null;
        }
    }

    function reconcileOwner() {
        // The generic API cache has no consumers and was historically unscoped.
        // Remove its retired bytes even when the current owner still matches.
        clearRetiredLocalCaches();

        var authState = documentAuthState();
        if (authState === "unknown") return;

        var sessionStore = getStorage("sessionStorage");
        var owner = currentOwner();
        if (authState === "authenticated" && !owner) {
            hideBodyForBfcache();
            clearPrivateBrowserCaches();
            try {
                window.location.reload();
            } catch (_error) {
                // Keep a malformed authenticated document hidden.
            }
            return;
        }
        if (!sessionStore) {
            // Without the session owner marker, persistent private bytes cannot
            // be proven to belong to this page. Fail closed.
            clearPrivateLocalCaches();
            clearServiceWorkerAuthCache();
            return;
        }

        var previousOwner = storedOwner(sessionStore);
        var pendingLogoutRaw = storedValue(sessionStore, LOGOUT_PENDING_KEY);
        var pendingLogout = validTransitionMarker(
            pendingLogoutRaw,
            "logout_pending"
        );
        var pendingLogoutMatches = Boolean(
            pendingLogout
            && normalizedOwner(
                String(pendingLogout.owner || "").split(":", 1)[0],
                String(pendingLogout.owner || "").split(":").slice(1).join(":")
            ) === owner
        );
        if (authState === "anonymous" || previousOwner !== owner) {
            clearPrivateBrowserCaches();
            if (previousOwner) broadcastPrivateCachePurge("owner_changed");
        }
        if (authState === "anonymous") return;
        if (pendingLogoutMatches) {
            setStoredValue(sessionStore, LOGOUT_PENDING_KEY, pendingLogoutRaw);
        }
        try {
            sessionStore.setItem(OWNER_KEY, owner);
        } catch (_error) {
            clearPrivateLocalCaches();
        }
    }

    function isLogoutTarget(target) {
        if (!target) return false;
        if (
            typeof target.matches === "function"
            && target.matches('form[action="/logout"]')
        ) return true;
        return (
            typeof target.closest === "function"
            && Boolean(target.closest('form[action="/logout"]'))
        );
    }

    function formForTarget(target) {
        if (!target) return null;
        if (String(target.tagName || "").toUpperCase() === "FORM") return target;
        if (typeof target.closest === "function") return target.closest("form");
        return null;
    }

    function formActionPath(form) {
        if (!form || typeof form.getAttribute !== "function") return "";
        var action = String(form.getAttribute("action") || "").trim();
        if (!action || action.indexOf("//") === 0 || /^[a-z]+:/i.test(action)) return "";
        return (action.split(/[?#]/, 1)[0] || "").replace(/\/+$/, "") || "/";
    }

    function isLoginTarget(target) {
        return formActionPath(formForTarget(target)) === "/login";
    }

    function markLoginPending() {
        setStoredValue(
            getStorage("sessionStorage"),
            LOGIN_PENDING_KEY,
            transitionMarker("login_pending")
        );
    }

    function markLogoutPending() {
        if (logoutPendingSent) return;
        logoutPendingSent = true;
        var owner = currentOwner();
        clearPrivateBrowserCaches();
        var marker = JSON.stringify({
            reason: "logout_pending",
            owner: owner,
            at: Date.now(),
            nonce: Math.random().toString(36).slice(2)
        });
        setStoredValue(getStorage("sessionStorage"), LOGOUT_PENDING_KEY, marker);
        broadcastLogoutPending();
    }

    function hideBodyForBfcache() {
        if (!document.body) return;
        if (bodyHiddenBeforeBfcache === null) {
            bodyHiddenBeforeBfcache = Boolean(document.body.hidden);
        }
        document.body.hidden = true;
    }

    function revealBodyAfterBfcache() {
        if (!document.body) return;
        document.body.hidden = bodyHiddenBeforeBfcache === true;
        bodyHiddenBeforeBfcache = null;
    }

    function failClosedBfcacheRestore() {
        clearPrivateBrowserCaches();
        broadcastPrivateCachePurge("bfcache_owner_mismatch");
        try {
            window.location.reload();
        } catch (_error) {
            // Keep the stale body hidden if navigation is blocked.
        }
        return false;
    }

    function serverOwner(payload) {
        if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
        return normalizedOwner(payload.id, payload.role);
    }

    function fetchAuthoritativeOwner() {
        var AbortControllerConstructor = window.AbortController;
        if (
            typeof window.fetch !== "function"
            || typeof AbortControllerConstructor !== "function"
        ) {
            return Promise.reject(new Error("owner_revalidation_unavailable"));
        }
        var controller = new AbortControllerConstructor();
        var timeoutId = window.setTimeout(function () {
            controller.abort();
        }, OWNER_REVALIDATION_TIMEOUT_MS);
        return window.fetch(OWNER_ENDPOINT, {
            method: "GET",
            credentials: "same-origin",
            cache: "no-store",
            headers: { "Accept": "application/json" },
            signal: controller.signal
        }).then(function (response) {
            if (response && (response.status === 401 || response.status === 403)) {
                return null;
            }
            if (!response || !response.ok) {
                throw new Error("owner_revalidation_failed");
            }
            return response.json().then(function (payload) {
                var owner = serverOwner(payload);
                if (!owner) throw new Error("owner_revalidation_malformed");
                return owner;
            });
        }).finally(function () {
            window.clearTimeout(timeoutId);
        });
    }

    function restoreVerifiedOwner(owner) {
        var sessionStore = getStorage("sessionStorage");
        removeStoredValue(sessionStore, LOGOUT_PENDING_KEY);
        setStoredValue(sessionStore, OWNER_KEY, owner);
        revealBodyAfterBfcache();
    }

    function verifyExternalLogout(epoch, expectedOwner, startedAt, attempt) {
        if (epoch !== logoutVerificationEpoch || externalPurgeHandled) return;
        var elapsed = Date.now() - startedAt;
        var delay = elapsed >= AUTH_TRANSITION_MAX_AGE_MS
            ? 0
            : LOGOUT_REVALIDATION_DELAYS_MS[
                Math.min(attempt, LOGOUT_REVALIDATION_DELAYS_MS.length - 1)
            ];
        window.setTimeout(function () {
            if (epoch !== logoutVerificationEpoch || externalPurgeHandled) return;
            fetchAuthoritativeOwner().then(function (authoritativeOwner) {
                if (epoch !== logoutVerificationEpoch || externalPurgeHandled) return;
                if (authoritativeOwner !== expectedOwner) {
                    reloadAfterExternalPurge({
                        type: "PURGE_PRIVATE_CACHES",
                        reason: "logout_authoritatively_completed"
                    });
                    return;
                }
                if (
                    Date.now() - startedAt >= AUTH_TRANSITION_MAX_AGE_MS
                    || attempt >= LOGOUT_REVALIDATION_DELAYS_MS.length - 1
                ) {
                    // Frontend requests are bounded to 30 seconds server-side.
                    // A same-owner response after that bound proves logout
                    // failed/cancelled, so revealing this owner's DOM is safe.
                    restoreVerifiedOwner(expectedOwner);
                    return;
                }
                verifyExternalLogout(epoch, expectedOwner, startedAt, attempt + 1);
            }).catch(function () {
                if (epoch !== logoutVerificationEpoch || externalPurgeHandled) return;
                // No authoritative answer means no reveal. Retry slowly and
                // leave the document hidden during an outage.
                window.setTimeout(function () {
                    verifyExternalLogout(epoch, expectedOwner, startedAt, attempt);
                }, 5000);
            });
        }, delay);
    }

    function startExternalLogoutVerification(expectedOwner, startedAt, nonce) {
        if (
            documentAuthState() !== "authenticated"
            || !expectedOwner
            || externalPurgeHandled
        ) return;
        logoutVerificationEpoch += 1;
        var epoch = logoutVerificationEpoch;
        hideBodyForBfcache();
        clearPrivateBrowserCaches();
        setStoredValue(
            getStorage("sessionStorage"),
            LOGOUT_PENDING_KEY,
            JSON.stringify({
                reason: "logout_pending",
                owner: expectedOwner,
                at: startedAt,
                nonce: nonce
            })
        );
        verifyExternalLogout(epoch, expectedOwner, startedAt, 0);
    }

    function beginExternalLogoutVerification(payload) {
        var owner = currentOwner();
        if (!owner || documentAuthState() !== "authenticated") return;
        var startedAt = Number(payload.at);
        if (
            !Number.isFinite(startedAt)
            || startedAt > Date.now() + 5000
            || Date.now() - startedAt > AUTH_TRANSITION_MAX_AGE_MS
        ) return;
        startExternalLogoutVerification(
            owner,
            startedAt,
            String(payload.nonce || "")
        );
    }

    function resumeExternalLogoutVerification() {
        if (documentAuthState() !== "authenticated") return;
        var sessionStore = getStorage("sessionStorage");
        var rawMarker = storedValue(sessionStore, LOGOUT_PENDING_KEY);
        var marker = validTransitionMarker(rawMarker, "logout_pending");
        var owner = currentOwner();
        if (!marker || marker.owner !== owner) {
            removeStoredValue(sessionStore, LOGOUT_PENDING_KEY);
            return;
        }
        startExternalLogoutVerification(
            owner,
            Number(marker.at),
            String(marker.nonce || "")
        );
    }

    function revalidateBfcacheOwner(event) {
        if (!event || event.persisted !== true) return Promise.resolve(false);
        hideBodyForBfcache();

        var expectedOwner = currentOwner();
        if (!expectedOwner) {
            if (documentAuthState() === "authenticated") {
                return Promise.resolve(failClosedBfcacheRestore());
            }
            if (documentAuthState() === "anonymous") clearPrivateBrowserCaches();
            revealBodyAfterBfcache();
            return Promise.resolve(false);
        }

        var sessionStore = getStorage("sessionStorage");
        if (!sessionStore || storedOwner(sessionStore) !== expectedOwner) {
            return Promise.resolve(failClosedBfcacheRestore());
        }

        var AbortControllerConstructor = window.AbortController;
        if (
            typeof window.fetch !== "function"
            || typeof AbortControllerConstructor !== "function"
        ) {
            return Promise.resolve(failClosedBfcacheRestore());
        }

        var controller = new AbortControllerConstructor();
        var timeoutId = window.setTimeout(function () {
            controller.abort();
        }, OWNER_REVALIDATION_TIMEOUT_MS);

        return window.fetch(OWNER_ENDPOINT, {
            method: "GET",
            credentials: "same-origin",
            cache: "no-store",
            headers: { "Accept": "application/json" },
            signal: controller.signal
        }).then(function (response) {
            if (!response || !response.ok) throw new Error("owner_revalidation_failed");
            return response.json();
        }).then(function (payload) {
            if (
                serverOwner(payload) !== expectedOwner
                || storedOwner(sessionStore) !== expectedOwner
            ) {
                return failClosedBfcacheRestore();
            }
            revealBodyAfterBfcache();
            return true;
        }).catch(function () {
            return failClosedBfcacheRestore();
        }).finally(function () {
            window.clearTimeout(timeoutId);
        });
    }

    if (
        window.PrivateBrowserCache
        && typeof window.PrivateBrowserCache.reconcileOwner === "function"
    ) {
        window.PrivateBrowserCache.reconcileOwner();
        return;
    }

    try {
        if (typeof window.BroadcastChannel === "function") {
            purgeChannel = new window.BroadcastChannel(PURGE_CHANNEL_NAME);
            purgeChannel.addEventListener("message", function (event) {
                if (event && event.data) handleCrossTabSignal(event.data);
            });
        }
    } catch (_error) {
        purgeChannel = null;
    }
    window.addEventListener("storage", function (event) {
        if (event && event.key === PURGE_SIGNAL_KEY && event.newValue) {
            var payload = parsedStorageSignal(event.newValue);
            if (payload) handleCrossTabSignal(payload);
        }
    });

    // Capture click before base.js calls native form.submit(), which does not
    // emit submit. Keep submit handling for keyboard/non-JS logout.
    document.addEventListener("click", function (event) {
        if (isLogoutTarget(event.target)) {
            markLogoutPending();
        }
    }, true);
    document.addEventListener("submit", function (event) {
        if (isLogoutTarget(event.target)) {
            markLogoutPending();
        } else if (isLoginTarget(event.target)) {
            markLoginPending();
        }
    }, true);
    document.addEventListener("app:auth-expired", function () {
        clearPrivateBrowserCaches();
        broadcastPrivateCachePurge("auth_expired");
    });
    window.addEventListener("pagehide", function (event) {
        if (
            event.persisted === true
            && documentAuthState() === "authenticated"
            && currentOwner()
        ) hideBodyForBfcache();
    });
    window.addEventListener("pageshow", function (event) {
        void revalidateBfcacheOwner(event);
    });

    var api = Object.freeze({
        clear: clearPrivateBrowserCaches,
        reconcileOwner: reconcileOwner,
        revalidateBfcacheOwner: revalidateBfcacheOwner
    });
    window.PrivateBrowserCache = api;
    if (typeof module !== "undefined" && module.exports) module.exports = api;

    reconcileOwner();
    completeServerConfirmedLogout();
    completeServerConfirmedLogin();
    resumeExternalLogoutVerification();
})();
